import { useEffect, useRef, useState } from "react";
import { Plus, Trash2, Image as ImageIcon } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import { api, formatApiErrorDetail, API } from "@/lib/api";

export default function BeforeAfter() {
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [order, setOrder] = useState(0);
  const [uploading, setUploading] = useState(false);
  const beforeRef = useRef(null);
  const afterRef = useRef(null);

  const load = () => api.get("/admin/results").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    e.preventDefault();
    const beforeFile = beforeRef.current?.files?.[0];
    const afterFile = afterRef.current?.files?.[0];
    if (!beforeFile || !afterFile) return toast.error("Selecione as duas fotos (antes e depois)");
    const fd = new FormData();
    fd.append("before_file", beforeFile);
    fd.append("after_file", afterFile);
    fd.append("title", title);
    fd.append("order", order);
    setUploading(true);
    try {
      await api.post("/admin/results", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Resultado enviado!");
      setTitle(""); setOrder(0); beforeRef.current.value = ""; afterRef.current.value = "";
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setUploading(false); }
  };

  const toggleActive = async (r) => {
    await api.patch(`/admin/results/${r.id}`, { active: !r.active });
    load();
  };

  const remove = async (id) => {
    await api.delete(`/admin/results/${id}`);
    toast.success("Resultado removido");
    load();
  };

  return (
    <div data-testid="admin-results-page">
      <h1 className="text-2xl font-serif text-[#9B6179] mb-6">Antes & Depois</h1>

      <div className="bg-white rounded-[2px] p-6 shadow-sm border border-[#9B6179]/5 mb-8">
        <p className="text-sm font-medium text-[#9B6179] mb-4">Novo resultado</p>
        <form onSubmit={upload} className="grid sm:grid-cols-4 gap-3 items-end">
          <div className="sm:col-span-2"><Label>Título (opcional)</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex: Peeling facial" data-testid="result-form-title" /></div>
          <div><Label>Ordem</Label><Input type="number" value={order} onChange={(e) => setOrder(e.target.value)} data-testid="result-form-order" /></div>
          <div><Label>Foto ANTES</Label><input ref={beforeRef} type="file" accept="image/*" className="text-sm block mt-1" data-testid="result-form-before-file" /></div>
          <div className="sm:col-start-1"><Label>Foto DEPOIS</Label><input ref={afterRef} type="file" accept="image/*" className="text-sm block mt-1" data-testid="result-form-after-file" /></div>
          <Button disabled={uploading} type="submit" className="sm:col-span-2 bg-[#090708] text-[#E8C47A] rounded-[2px] hover:bg-[#6F3F55] transition-colors" data-testid="result-form-submit">
            <Plus className="w-4 h-4 mr-1" /> {uploading ? "Enviando..." : "Enviar resultado"}
          </Button>
        </form>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((r) => (
          <div key={r.id} className="bg-white rounded-[2px] p-4 shadow-sm border border-[#9B6179]/5" data-testid={`result-card-${r.id}`}>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <img src={`${API}/public/results/${r.id}/before`} alt="antes" className="w-full h-32 object-cover rounded-[2px] bg-[#F7F0E8]" />
              <img src={`${API}/public/results/${r.id}/after`} alt="depois" className="w-full h-32 object-cover rounded-[2px] bg-[#F7F0E8]" />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-[#9B6179]">{r.title || "Sem título"}</p>
                <p className="text-xs text-[#6F3F55]">Ordem: {r.order}</p>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={r.active} onCheckedChange={() => toggleActive(r)} data-testid={`result-toggle-${r.id}`} />
                <button onClick={() => remove(r.id)} data-testid={`result-delete-${r.id}`}><Trash2 className="w-4 h-4 text-[#6F3F55]" /></button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <p className="text-sm text-[#6F3F55] col-span-full flex items-center gap-2"><ImageIcon className="w-4 h-4" /> Nenhum resultado antes/depois ainda.</p>
        )}
      </div>
    </div>
  );
}
